import { useMemo, useRef, memo, useEffect, useState } from "react";
import * as THREE from "three";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { Text } from "@react-three/drei";
import { useThree, useFrame } from "@react-three/fiber";
import gsap from "gsap";
import { createBuildingShaderMaterial } from "../../materials/buildingShaderMaterial";
import {
  createBuildingEdgesGeometry,
  calculateBoundingBox,
} from "../../utils/buildingGeometry";
import {
  useBuildingExplode,
  calculateWallOffsets,
} from "../../hooks/useBuildingExplode";
import {
  useTextPositionMode,
  useBuildingDetailStore,
} from "@/stores/buildingDetailStore";
import { useTenantQuery } from "@/hooks/useTenantQuery";
import type { BuildingStructureBox } from "@/utils/supabase";

/**
 * GUI 클리핑 영역에서 숨겨지는 Text 컴포넌트
 * 텍스트 중심이 GUI 삼각형 영역에 들어가면 완전히 숨김
 */
interface HiddenInGUITextProps {
  text: string;
  position: [number, number, number];
  rotation: THREE.Euler | [number, number, number];
  fontSize: number;
  maxWidth: number;
  userColor: string;
}

function HiddenInGUIText({
  text,
  position,
  rotation,
  fontSize,
  maxWidth,
  userColor,
}: HiddenInGUITextProps) {
  const { camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const [isVisible, setIsVisible] = useState(true);

  // GUI 클리핑 체크용 함수
  const checkGUIClipping = useMemo(() => {
    return (ndc: THREE.Vector3, controlPoint: number = 0.5) => {
      const controlX = controlPoint * 2 - 1; // 0.5 -> 0

      // 등각 투영 방향 (30도)
      const worldXDir = { x: 0.866, y: -0.5 };
      const worldZDir = { x: -0.866, y: -0.5 };

      // sideOfLine 계산
      const sideOfLine = (
        lineX: number,
        lineY: number,
        dirX: number,
        dirY: number,
        px: number,
        py: number
      ) => {
        const perpX = -dirY;
        const perpY = dirX;
        return (px - lineX) * perpX + (py - lineY) * perpY;
      };

      // 4개 모서리 체크
      const inTopLeft =
        sideOfLine(controlX, 1, worldZDir.x, worldZDir.y, ndc.x, ndc.y) < 0;
      const inTopRight =
        sideOfLine(controlX, 1, worldXDir.x, worldXDir.y, ndc.x, ndc.y) > 0;
      const inBottomLeft =
        sideOfLine(controlX, -1, worldXDir.x, worldXDir.y, ndc.x, ndc.y) < 0;
      const inBottomRight =
        sideOfLine(controlX, -1, worldZDir.x, worldZDir.y, ndc.x, ndc.y) > 0;

      return inTopLeft || inTopRight || inBottomLeft || inBottomRight;
    };
  }, []);

  // 매 프레임마다 GUI 영역 체크 (텍스트 바운딩 박스의 4개 코너 체크)
  useFrame(() => {
    if (!groupRef.current) return;

    // 그룹의 월드 위치 (텍스트 중심)
    const worldPos = new THREE.Vector3();
    groupRef.current.getWorldPosition(worldPos);

    // 텍스트 크기 추정 (가로: 글자수 * fontSize, 세로: fontSize)
    const estimatedWidth = Math.min(text.length * fontSize * 0.6, maxWidth);
    const estimatedHeight = fontSize * 1.2;

    // 4개 코너 위치 계산 (로컬 오프셋)
    const halfW = estimatedWidth / 2;
    const halfH = estimatedHeight / 2;
    const corners = [
      new THREE.Vector3(worldPos.x - halfW, worldPos.y + halfH, worldPos.z), // 좌상
      new THREE.Vector3(worldPos.x + halfW, worldPos.y + halfH, worldPos.z), // 우상
      new THREE.Vector3(worldPos.x - halfW, worldPos.y - halfH, worldPos.z), // 좌하
      new THREE.Vector3(worldPos.x + halfW, worldPos.y - halfH, worldPos.z), // 우하
    ];

    // 하나라도 GUI 영역에 닿으면 숨김
    let anyInGUI = false;
    for (const corner of corners) {
      const ndc = corner.clone().project(camera);
      if (checkGUIClipping(ndc)) {
        anyInGUI = true;
        break;
      }
    }

    if (isVisible !== !anyInGUI) {
      setIsVisible(!anyInGUI);
    }
  });

  return (
    <group ref={groupRef} position={position} rotation={rotation}>
      <Text
        visible={isVisible}
        font={import.meta.env.BASE_URL + "/font.woff"}
        fontSize={fontSize}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.0}
        outlineOpacity={0}
        maxWidth={maxWidth}
        textAlign="center"
        renderOrder={100} // 벽면(기본 0)보다 훨씬 나중에 그리도록 설정
      >
        {text}
      </Text>
    </group>
  );
}

interface ExplodedBuildingModelProps {
  id: string;
  position: [number, number, number];
  structure: BuildingStructureBox[];
  userColor: string;
  isSelected: boolean;
  opacity?: number;
}

// 면 방향 타입
type FaceDirection = "front" | "back" | "left" | "right" | "top" | "bottom";

interface WallMeshData {
  geometry: THREE.BufferGeometry;
  direction: FaceDirection;
  normal: THREE.Vector3;
  center: THREE.Vector3;
}

/**
 * 박스 구조에서 각 면별 지오메트리 생성
 */
function createWallGeometries(
  structure: BuildingStructureBox[]
): WallMeshData[] {
  const walls: WallMeshData[] = [];

  structure.forEach((box) => {
    const [px, py, pz] = box.position;
    const [sx, sy, sz] = box.scale;
    const halfX = sx / 2;
    const halfY = sy / 2;
    const halfZ = sz / 2;

    // 각 면에 대한 PlaneGeometry 생성
    const faces: {
      direction: FaceDirection;
      normal: THREE.Vector3;
      size: [number, number];
      position: THREE.Vector3;
      rotation: THREE.Euler;
    }[] = [
      // Front (Z+)
      {
        direction: "front",
        normal: new THREE.Vector3(0, 0, 1),
        size: [sx, sy],
        position: new THREE.Vector3(px, py, pz + halfZ),
        rotation: new THREE.Euler(0, 0, 0),
      },
      // Back (Z-)
      {
        direction: "back",
        normal: new THREE.Vector3(0, 0, -1),
        size: [sx, sy],
        position: new THREE.Vector3(px, py, pz - halfZ),
        rotation: new THREE.Euler(0, Math.PI, 0),
      },
      // Right (X+)
      {
        direction: "right",
        normal: new THREE.Vector3(1, 0, 0),
        size: [sz, sy],
        position: new THREE.Vector3(px + halfX, py, pz),
        rotation: new THREE.Euler(0, Math.PI / 2, 0),
      },
      // Left (X-)
      {
        direction: "left",
        normal: new THREE.Vector3(-1, 0, 0),
        size: [sz, sy],
        position: new THREE.Vector3(px - halfX, py, pz),
        rotation: new THREE.Euler(0, -Math.PI / 2, 0),
      },
      // Top (Y+)
      {
        direction: "top",
        normal: new THREE.Vector3(0, 1, 0),
        size: [sx, sz],
        position: new THREE.Vector3(px, py + halfY, pz),
        rotation: new THREE.Euler(-Math.PI / 2, 0, 0),
      },
      // Bottom (Y-)
      {
        direction: "bottom",
        normal: new THREE.Vector3(0, -1, 0),
        size: [sx, sz],
        position: new THREE.Vector3(px, py - halfY, pz),
        rotation: new THREE.Euler(Math.PI / 2, 0, 0),
      },
    ];

    faces.forEach(({ direction, normal, size, position, rotation }) => {
      const geometry = new THREE.PlaneGeometry(size[0], size[1]);

      // 위치와 회전 적용
      geometry.rotateX(rotation.x);
      geometry.rotateY(rotation.y);
      geometry.rotateZ(rotation.z);
      geometry.translate(position.x, position.y, position.z);

      walls.push({
        geometry,
        direction,
        normal: normal.clone(),
        center: position.clone(),
      });
    });
  });

  return walls;
}

/**
 * 폭발하는 건물 모델 컴포넌트
 * 건물 선택 시 정면과 측면 벽이 노말 방향으로 날아가고
 * 내부에서 텍스트가 튀어나오는 애니메이션
 */
export const ExplodedBuildingModel = memo(function ExplodedBuildingModel({
  id,
  position,
  structure,
  userColor,
  isSelected,
  opacity = 1.0,
}: ExplodedBuildingModelProps) {
  const groupRef = useRef<THREE.Group>(null);

  // 실시간 입주자 데이터 (TanStack Query 사용)
  const { data: tenants = [] } = useTenantQuery(id);

  // 폭발 애니메이이션 상태 (지퍼 가동 중이면 폭발 처리)
  const zipperProgress = useBuildingDetailStore(
    (state) => state.zipperProgress
  );
  const shouldExplode = isSelected && zipperProgress < 0.5;

  // 폭발 애니메이션 상태 (지퍼 progress와 직접 연동)
  // progress: 0.5(닫힘) -> 0.0(열림) 일 때 폭발 진행률 0 -> 1
  const explosionProgress = useMemo(() => {
    if (!shouldExplode) return 0;
    return Math.max(0, (0.5 - zipperProgress) / 0.5);
  }, [zipperProgress, shouldExplode]);

  // 기존 훅의 리턴값 중 progress만 explosionProgress로 대체
  const { progress: _hookProgress, isExploded: _isExploded } =
    useBuildingExplode(shouldExplode);

  // 최종 progress는 지퍼 연동값 사용
  const progress = explosionProgress;

  // 바운딩 박스 계산
  const boundingBox = useMemo(
    () => calculateBoundingBox(structure),
    [structure]
  );
  const buildingSize = useMemo(
    () => ({
      width: boundingBox.maxX - boundingBox.minX,
      depth: boundingBox.maxZ - boundingBox.minZ,
      height: boundingBox.maxY - boundingBox.minY,
    }),
    [boundingBox]
  );

  // 각 면별 지오메트리 생성
  const wallMeshes = useMemo(
    () => createWallGeometries(structure),
    [structure]
  );

  // 면별로 그룹화 (front/right만 폭발)
  const { explodingWalls, staticWalls } = useMemo(() => {
    const exploding: WallMeshData[] = [];
    const static_: WallMeshData[] = [];

    wallMeshes.forEach((wall) => {
      if (wall.direction === "front" || wall.direction === "right") {
        exploding.push(wall);
      } else {
        static_.push(wall);
      }
    });

    return { explodingWalls: exploding, staticWalls: static_ };
  }, [wallMeshes]);

  // 정적 벽면들을 하나의 지오메트리로 합치기
  const mergedStaticGeometry = useMemo(() => {
    if (staticWalls.length === 0) return null;
    const geometries = staticWalls.map((wall) => wall.geometry);
    return BufferGeometryUtils.mergeGeometries(geometries, false);
  }, [staticWalls]);

  // 폭발하는 벽면들을 방향별로 합치기 (front와 right 벽면은 다른 방향으로 이동)
  const { mergedFrontGeometry, mergedRightGeometry } = useMemo(() => {
    const frontWalls = explodingWalls.filter((w) => w.direction === "front");
    const rightWalls = explodingWalls.filter((w) => w.direction === "right");

    const mergedFront =
      frontWalls.length > 0
        ? BufferGeometryUtils.mergeGeometries(
            frontWalls.map((w) => w.geometry),
            false
          )
        : null;

    const mergedRight =
      rightWalls.length > 0
        ? BufferGeometryUtils.mergeGeometries(
            rightWalls.map((w) => w.geometry),
            false
          )
        : null;

    return {
      mergedFrontGeometry: mergedFront,
      mergedRightGeometry: mergedRight,
    };
  }, [explodingWalls]);
  // 폭발 오프셋 계산
  const explodeOffsets = useMemo(
    () => calculateWallOffsets(progress, buildingSize, 2.5),
    [progress, buildingSize]
  );

  // 건물 ID를 해시해서 일관된 시드 생성 (0~1000 범위, BuildingModel과 동일)
  const buildingSeed = useMemo(() => {
    let seed = 0;
    for (let i = 0; i < id.length; i++) {
      seed = ((seed << 5) - seed + id.charCodeAt(i)) | 0;
    }
    return Math.abs(seed) % 1000;
  }, [id]);

  // 폭발하는 벽면 materials (DoubleSide)
  // stencil 설정을 추가하여 GUI Triangle 위에 렌더링되도록 함
  // wallOpacity에 따라 투명도 변경
  const explodeMaterial = useMemo(() => {
    const mat = createBuildingShaderMaterial({
      userColor,
      doubleSide: true,
      backFaceDarkness: 0.2,
      opacity: opacity * explodeOffsets.wallOpacity, // 폭발 시 opacity 적용
      buildingSeed,
    });
    // Stencil test 무시 (GUI Triangle stencil 영역 위에 렌더링)
    mat.stencilWrite = false;
    mat.stencilFunc = THREE.AlwaysStencilFunc;
    // 투명도가 1보다 작으면 transparent 활성화
    mat.transparent = explodeOffsets.wallOpacity < 1;
    return mat;
  }, [userColor, opacity, explodeOffsets.wallOpacity, buildingSeed]);

  // 정적 벽면 material
  const staticMaterial = useMemo(() => {
    const mat = createBuildingShaderMaterial({
      userColor,
      doubleSide: true,
      backFaceDarkness: 0.25,
      opacity,
      buildingSeed,
    });
    // Stencil test 무시 (GUI Triangle stencil 영역 위에 렌더링)
    mat.stencilWrite = false;
    mat.stencilFunc = THREE.AlwaysStencilFunc;
    return mat;
  }, [userColor, opacity, buildingSeed]);

  // 엣지 지오메트리
  const edgesGeometry = useMemo(
    () => createBuildingEdgesGeometry(structure),
    [structure]
  );

  // 엣지 머티리얼 (GUI 클리핑 지원 ShaderMaterial)
  const edgeMaterial = useMemo(() => {
    const color = new THREE.Color(userColor).multiplyScalar(0.5);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: color },
        opacity: { value: opacity },
        // GUI 클리핑용 uniforms
        uGUIControlPoint: { value: 0.5 },
        uWorldXDir: { value: new THREE.Vector2(0.866, -0.5) },
        uWorldZDir: { value: new THREE.Vector2(-0.866, -0.5) },
        uEnableGUIClipping: { value: 0.0 },
        uGUIClippingDebug: { value: 0.0 },
        // 각 코너별 enabled (기본값: 모두 활성화)
        uTopLeftEnabled: { value: 1.0 },
        uTopRightEnabled: { value: 1.0 },
        uBottomLeftEnabled: { value: 1.0 },
        uBottomRightEnabled: { value: 1.0 },
        // 각 코너별 hypotenuseOffset (기본값: 0.0 = 원래 위치)
        uTopLeftOffset: { value: 0.0 },
        uTopRightOffset: { value: 0.0 },
        uBottomLeftOffset: { value: 0.0 },
        uBottomRightOffset: { value: 0.0 },
      },
      vertexShader: `
        varying vec4 vGroundClipPos;
        void main() {
          vec4 groundPos = vec4(position.x, 0.0, position.z, 1.0);
          vGroundClipPos = projectionMatrix * modelViewMatrix * groundPos;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float opacity;
        uniform float uGUIControlPoint;
        uniform vec2 uWorldXDir;
        uniform vec2 uWorldZDir;
        uniform float uEnableGUIClipping;
        uniform float uGUIClippingDebug;
        uniform float uTopLeftEnabled;
        uniform float uTopRightEnabled;
        uniform float uBottomLeftEnabled;
        uniform float uBottomRightEnabled;
        uniform float uTopLeftOffset;
        uniform float uTopRightOffset;
        uniform float uBottomLeftOffset;
        uniform float uBottomRightOffset;
        varying vec4 vGroundClipPos;
        
        float sideOfLine(vec2 linePoint, vec2 lineDir, vec2 point) {
          vec2 perp = vec2(-lineDir.y, lineDir.x);
          return dot(point - linePoint, perp);
        }
        
        void main() {
          if (uEnableGUIClipping > 0.5) {
            vec2 ndc = vGroundClipPos.xy / vGroundClipPos.w;
            float controlX = uGUIControlPoint * 2.0 - 1.0;
            
            // TopLeft (hypotenuseOffset 적용)
            vec2 topLeftCorner = vec2(-1.0, 1.0);
            vec2 baseTopLeftControl = vec2(controlX, 1.0);
            vec2 topLeftPerp = normalize(vec2(uWorldZDir.y, -uWorldZDir.x));
            float topLeftDistToCorner = length(baseTopLeftControl - topLeftCorner);
            vec2 topLeftControl = baseTopLeftControl + topLeftPerp * uTopLeftOffset * topLeftDistToCorner * 1.5;
            if (sideOfLine(topLeftControl, uWorldZDir, ndc) < 0.0 && uTopLeftEnabled > 0.5) {
              if (uGUIClippingDebug > 0.5) { gl_FragColor = vec4(1.0, 0.0, 0.0, opacity); return; }
              discard;
            }
            
            // TopRight (hypotenuseOffset 적용)
            vec2 topRightCorner = vec2(1.0, 1.0);
            vec2 baseTopRightControl = vec2(controlX, 1.0);
            vec2 topRightPerp = normalize(vec2(-uWorldXDir.y, uWorldXDir.x));
            float topRightDistToCorner = length(baseTopRightControl - topRightCorner);
            vec2 topRightControl = baseTopRightControl + topRightPerp * uTopRightOffset * topRightDistToCorner * 1.5;
            if (sideOfLine(topRightControl, uWorldXDir, ndc) > 0.0 && uTopRightEnabled > 0.5) {
              if (uGUIClippingDebug > 0.5) { gl_FragColor = vec4(0.0, 1.0, 0.0, opacity); return; }
              discard;
            }
            
            // BottomLeft (hypotenuseOffset 적용)
            vec2 bottomLeftCorner = vec2(-1.0, -1.0);
            vec2 baseBottomLeftControl = vec2(controlX, -1.0);
            vec2 bottomLeftPerp = normalize(vec2(uWorldXDir.y, -uWorldXDir.x));
            float bottomLeftDistToCorner = length(baseBottomLeftControl - bottomLeftCorner);
            vec2 bottomLeftControl = baseBottomLeftControl + bottomLeftPerp * uBottomLeftOffset * bottomLeftDistToCorner * 1.5;
            if (sideOfLine(bottomLeftControl, uWorldXDir, ndc) < 0.0 && uBottomLeftEnabled > 0.5) {
              if (uGUIClippingDebug > 0.5) { gl_FragColor = vec4(0.0, 0.0, 1.0, opacity); return; }
              discard;
            }
            
            // BottomRight (hypotenuseOffset 적용)
            vec2 bottomRightCorner = vec2(1.0, -1.0);
            vec2 baseBottomRightControl = vec2(controlX, -1.0);
            vec2 bottomRightPerp = normalize(vec2(-uWorldZDir.y, uWorldZDir.x));
            float bottomRightDistToCorner = length(baseBottomRightControl - bottomRightCorner);
            vec2 bottomRightControl = baseBottomRightControl + bottomRightPerp * uBottomRightOffset * bottomRightDistToCorner * 1.5;
            if (sideOfLine(bottomRightControl, uWorldZDir, ndc) > 0.0 && uBottomRightEnabled > 0.5) {
              if (uGUIClippingDebug > 0.5) { gl_FragColor = vec4(1.0, 1.0, 0.0, opacity); return; }
              discard;
            }
          }
          gl_FragColor = vec4(uColor, opacity);
        }
      `,
      // 항상 transparent로 설정하여 GUI Triangle과 같은 pass에서 renderOrder로 정렬
      transparent: true,
    });
    // Stencil test 무시 (GUI Triangle stencil 영역 위에 렌더링)
    mat.stencilWrite = false;
    mat.stencilFunc = THREE.AlwaysStencilFunc;
    return mat;
  }, [userColor, opacity]);

  // 텍스트 위치 모드 (향후 확장용)
  const textMode = useTextPositionMode();

  // AABB 충돌 검사를 위한 인터페이스
  interface TextBoundingBox {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    minZ: number;
    maxZ: number;
  }

  // 두 바운딩 박스가 겹치는지 확인
  const checkAABBCollision = (
    a: TextBoundingBox,
    b: TextBoundingBox,
    padding: number = 0.1
  ): boolean => {
    return (
      a.minX - padding < b.maxX + padding &&
      a.maxX + padding > b.minX - padding &&
      a.minY - padding < b.maxY + padding &&
      a.maxY + padding > b.minY - padding &&
      a.minZ - padding < b.maxZ + padding &&
      a.maxZ + padding > b.minZ - padding
    );
  };

  // 텍스트 위치에서 바운딩 박스 계산
  const getTextBoundingBox = (
    position: [number, number, number],
    textLength: number,
    fontSize: number,
    isXAxis: boolean,
    maxWidth: number
  ): TextBoundingBox => {
    const estimatedWidth = Math.min(textLength * fontSize * 0.6, maxWidth);
    const estimatedHeight = fontSize * 1.2;
    const textDepth = 0.1;

    if (isXAxis) {
      // X+ 방향 벽면: 텍스트가 Z방향으로 펼쳐짐
      return {
        minX: position[0] - textDepth / 2,
        maxX: position[0] + textDepth / 2,
        minY: position[1] - estimatedHeight / 2,
        maxY: position[1] + estimatedHeight / 2,
        minZ: position[2] - estimatedWidth / 2,
        maxZ: position[2] + estimatedWidth / 2,
      };
    } else {
      // Z+ 방향 벽면: 텍스트가 X방향으로 펼쳐짐
      return {
        minX: position[0] - estimatedWidth / 2,
        maxX: position[0] + estimatedWidth / 2,
        minY: position[1] - estimatedHeight / 2,
        maxY: position[1] + estimatedHeight / 2,
        minZ: position[2] - textDepth / 2,
        maxZ: position[2] + textDepth / 2,
      };
    }
  };

  // 각 입주자별 위치 계산 (AABB 충돌 검사로 겹침 방지)
  const tenantPositions = useMemo(() => {
    const widthX = boundingBox.maxX - boundingBox.minX;
    const widthZ = boundingBox.maxZ - boundingBox.minZ;
    const buildingHeight = boundingBox.maxY - boundingBox.minY;

    // 폰트 크기: 건물 높이 기준
    const fontSize = Math.max(0.05, Math.min(0.2, buildingHeight * 0.05));

    // 배치된 텍스트들의 바운딩 박스 저장
    const placedBoxes: { box: TextBoundingBox; isXAxis: boolean }[] = [];

    const result = tenants.map((tenant) => {
      const u = tenant.positionU;
      const v = tenant.positionV;

      const isNegativeAxis =
        tenant.projectionAxis === "-x" || tenant.projectionAxis === "-z";
      const normalizedAxis =
        tenant.projectionAxis === "-x"
          ? "x"
          : tenant.projectionAxis === "-z"
          ? "z"
          : tenant.projectionAxis;
      const normalizedU = isNegativeAxis ? 1 - u : u;
      const isXAxis = normalizedAxis === "x";

      // 실제 Y 위치 계산
      const actualY = boundingBox.minY + v * buildingHeight;

      let textPosition: [number, number, number];
      let textRotation: [number, number, number];
      const offset = 0.05;
      const maxWidth = isXAxis ? widthZ * 0.8 : widthX * 0.8;

      if (isXAxis) {
        // X+ 방향: 해당 Z, Y 위치에서 가장 오른쪽 벽 찾기
        const targetZ = boundingBox.minZ + normalizedU * widthZ;

        // 해당 Z, Y 위치를 포함하는 박스들 중 가장 큰 maxX 찾기
        let maxXAtPosition = boundingBox.minX;
        for (const box of structure) {
          const [px, py, pz] = box.position;
          const [sx, sy, sz] = box.scale;
          const boxMinZ = pz - sz / 2;
          const boxMaxZ = pz + sz / 2;
          const boxMinY = py - sy / 2;
          const boxMaxY = py + sy / 2;
          const boxMaxX = px + sx / 2;

          if (
            targetZ >= boxMinZ &&
            targetZ <= boxMaxZ &&
            actualY >= boxMinY &&
            actualY <= boxMaxY
          ) {
            maxXAtPosition = Math.max(maxXAtPosition, boxMaxX);
          }
        }

        textPosition = [maxXAtPosition + offset, actualY, targetZ];
        textRotation = [0, Math.PI / 2, 0];
      } else {
        // Z+ 방향: 해당 X, Y 위치에서 가장 앞쪽 벽 찾기
        const targetX = boundingBox.minX + normalizedU * widthX;

        // 해당 X, Y 위치를 포함하는 박스들 중 가장 큰 maxZ 찾기
        let maxZAtPosition = boundingBox.minZ;
        for (const box of structure) {
          const [px, py, pz] = box.position;
          const [sx, sy, sz] = box.scale;
          const boxMinX = px - sx / 2;
          const boxMaxX = px + sx / 2;
          const boxMinY = py - sy / 2;
          const boxMaxY = py + sy / 2;
          const boxMaxZ = pz + sz / 2;

          if (
            targetX >= boxMinX &&
            targetX <= boxMaxX &&
            actualY >= boxMinY &&
            actualY <= boxMaxY
          ) {
            maxZAtPosition = Math.max(maxZAtPosition, boxMaxZ);
          }
        }

        textPosition = [targetX, actualY, maxZAtPosition + offset];
        textRotation = [0, 0, 0];
      }

      // AABB 충돌 검사: 기존 텍스트들과 겹치는지 확인
      let currentBox = getTextBoundingBox(
        textPosition,
        tenant.text.length,
        fontSize,
        isXAxis,
        maxWidth
      );

      const maxAttempts = 20; // 무한 루프 방지
      const pushDistance = 0.8; // 벽면 방향으로 밀어내는 거리

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        let hasCollision = false;

        for (const placed of placedBoxes) {
          if (checkAABBCollision(currentBox, placed.box)) {
            hasCollision = true;
            break;
          }
        }

        if (!hasCollision) break;

        // 충돌 시 벽면 노말 방향으로 밀어냄 (X+ 또는 Z+ 방향)
        if (isXAxis) {
          // X+ 벽면: X 방향으로 밀어냄
          textPosition[0] += pushDistance;
        } else {
          // Z+ 벽면: Z 방향으로 밀어냄
          textPosition[2] += pushDistance;
        }

        // 바운딩 박스 업데이트
        currentBox = getTextBoundingBox(
          textPosition,
          tenant.text.length,
          fontSize,
          isXAxis,
          maxWidth
        );
      }

      // 배치 완료된 바운딩 박스 저장
      placedBoxes.push({ box: currentBox, isXAxis });

      return {
        tenant,
        position: textPosition,
        rotation: textRotation,
        fontSize,
        maxWidth,
      };
    });

    return result;
  }, [tenants, boundingBox, structure]);

  return (
    <group ref={groupRef} position={position}>
      {/* 정적 벽면들 (back, left, top, bottom) - 합쳐진 지오메트리 사용 */}
      {mergedStaticGeometry && (
        <mesh geometry={mergedStaticGeometry} material={staticMaterial} />
      )}

      {/* 폭발하는 벽면들 (front, right) - 합쳐진 지오메트리 사용 */}
      {mergedFrontGeometry && (
        <mesh
          geometry={mergedFrontGeometry}
          material={explodeMaterial}
          position={[0, 0, explodeOffsets.frontZ]}
        />
      )}
      {mergedRightGeometry && (
        <mesh
          geometry={mergedRightGeometry}
          material={explodeMaterial}
          position={[explodeOffsets.sideX, 0, 0]}
        />
      )}

      {/* 내부 텍스트 (지퍼가 완전히 열렸을 때만 등장 - 애니메이션 없이 즉시 표시) */}
      {shouldExplode &&
        zipperProgress <= 0.05 &&
        tenantPositions.map(
          (
            { tenant, position: textPos, rotation, fontSize, maxWidth },
            index
          ) => (
            <group key={tenant.id}>
              <HiddenInGUIText
                text={tenant.text}
                position={textPos}
                rotation={rotation}
                fontSize={fontSize}
                maxWidth={maxWidth}
                userColor={userColor}
              />
            </group>
          )
        )}

      {/* 정적 벽면 엣지 */}
      <lineSegments geometry={edgesGeometry} material={edgeMaterial} />
    </group>
  );
});
